import React, { useRef, useState } from 'react';
import clsx from 'clsx';
import styles from '../MagicSignTable.module.scss';
// Reuse Magic Upload's three-dots popover styles + icon so the menu is visually identical to Magic Upload.
import muStyles from '../../../../MagicUpload/components/MagicUploadFilesTable/MagicUploadFilesTable.module.scss';
import { MagicSignRow, MagicSignStatus } from '../../../types';
import ThreeDotsIcon from '../../../../../assets/Svg/ThreeDotsIcon';

interface ThreeDotsMenuProps {
    row: MagicSignRow;
    onGenerateLinks: (row: MagicSignRow) => void;
    onSendEmail: (row: MagicSignRow) => void;
    onViewAuditLog: (row: MagicSignRow) => void;
    onDownloadDocument: (row: MagicSignRow) => void;
    onCopySigningLinks: (row: MagicSignRow) => void;
    onCancelRequest: (id: string) => void;
}

/**
 * Status-aware row action menu. Renders with the SAME icon (ThreeDotsIcon) and popover styling
 * (.MoreOptions) Magic Upload uses, so the two tables' kebab menus look and behave identically.
 * Magic Sign only owns the rule for which actions appear per status.
 */
const ThreeDotsMenu: React.FC<ThreeDotsMenuProps> = ({
    row,
    onGenerateLinks,
    onSendEmail,
    onViewAuditLog,
    onDownloadDocument,
    onCopySigningLinks,
    onCancelRequest,
}) => {
    const [open, setOpen] = useState(false);
    const ref = useRef<HTMLDivElement>(null);

    const handleClickOutside = React.useCallback((e: MouseEvent) => {
        if (ref.current && !ref.current.contains(e.target as Node)) {
            setOpen(false);
        }
    }, []);

    React.useEffect(() => {
        if (open) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [open, handleClickOutside]);

    const close = () => setOpen(false);

    const showGenerateLinks = row.status === MagicSignStatus.UPLOADED || row.status === MagicSignStatus.GENERATED_LINKS;
    const showSendEmail = row.status === MagicSignStatus.GENERATED_LINKS && !row.emailSentAt;
    const showSendReminder =
        (row.status === MagicSignStatus.GENERATED_LINKS && !!row.emailSentAt) || row.status === MagicSignStatus.PARTIAL_SIGNED;
    const showCopyLink = row.status === MagicSignStatus.GENERATED_LINKS || row.status === MagicSignStatus.PARTIAL_SIGNED;
    const showCancel = row.status === MagicSignStatus.UPLOADED || row.status === MagicSignStatus.GENERATED_LINKS;
    // No document to download while it's still being uploaded or links are generating.
    const showDownload = row.status !== MagicSignStatus.UPLOADING && row.status !== MagicSignStatus.GENERATING_LINKS;

    return (
        <div className={styles.MenuPopoverContainer} ref={ref}>
            <span
                role="button"
                tabIndex={0}
                aria-label="Row actions"
                onClick={(e) => {
                    e.stopPropagation();
                    setOpen((prev) => !prev);
                }}
            >
                <ThreeDotsIcon colour={'var(--primary-text-colour)'} style={{ cursor: 'pointer' }} height="14" width="14" />
            </span>
            {open && (
                <div className={clsx(muStyles.MoreOptions, styles.MenuPosition)} role="menu">
                    {showGenerateLinks && (
                        <span
                            role="menuitem"
                            onMouseDown={(e) => {
                                e.preventDefault();
                                close();
                                onGenerateLinks(row);
                            }}
                        >
                            Generate Links
                        </span>
                    )}
                    {showSendEmail && (
                        <span
                            role="menuitem"
                            onMouseDown={(e) => {
                                e.preventDefault();
                                close();
                                onSendEmail(row);
                            }}
                        >
                            Send Email
                        </span>
                    )}
                    {showSendReminder && (
                        <span
                            role="menuitem"
                            onMouseDown={(e) => {
                                e.preventDefault();
                                close();
                                onSendEmail(row);
                            }}
                        >
                            Send Reminder
                        </span>
                    )}
                    {showCopyLink && (
                        <span
                            role="menuitem"
                            onMouseDown={(e) => {
                                e.preventDefault();
                                close();
                                onCopySigningLinks(row);
                            }}
                        >
                            Copy Signing Link
                        </span>
                    )}
                    {showDownload && (
                        <span
                            role="menuitem"
                            onMouseDown={(e) => {
                                e.preventDefault();
                                close();
                                onDownloadDocument(row);
                            }}
                        >
                            Download
                        </span>
                    )}
                    <span
                        role="menuitem"
                        onMouseDown={(e) => {
                            e.preventDefault();
                            close();
                            onViewAuditLog(row);
                        }}
                    >
                        Audit Log
                    </span>
                    {showCancel && (
                        <span
                            role="menuitem"
                            className={styles.Destructive}
                            onMouseDown={(e) => {
                                e.preventDefault();
                                close();
                                onCancelRequest(row.id);
                            }}
                        >
                            Cancel Signing Link
                        </span>
                    )}
                </div>
            )}
        </div>
    );
};

export default ThreeDotsMenu;
